// Chat info pieces of the mobile Pro chat (mobile InfoProParts.js), adapted to web:
// the 80 hero, the 4-column action tiles, the rows card, setting rows with a switch,
// the "MEMBERS · N" caption, member rows, the member action menu, the away banner,
// the inline edit box and the "Leave chat" button. Shared by the 1:1 info
// (pages/Chat/UserInfo) and the group info (pages/Project/UserInfo).
//
// Pressable tiles and rows are divs with role="button": public custom.css forces
// every `button:not(.kj-btn)` to 38px. Real buttons use the kj-btn system.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { callAPI } from "../../../helper/apiUtils";
import { convertPosition } from "../../../helper/convertor";
import ModernToggleSwitch from "../../ModernToggleSwitch";
import InfoTip from "../../InfoTip";
import teamsLogo from "../../../Icons/Teams.png";
import "./ChatInfoParts.css";

/** Existing copy first, then the language fallback. */
export const infoText = (strings, isGerm, key, de, en) => (strings && strings[key]) || (isGerm ? de : en);

/** "{n} …" templates. */
export const fillN = (template, n) => String(template || "").replace(/\{n\}/g, String(n));

/** Tone classes for an info page (internal rooms keep the internal colour). */
export const infoToneClass = (isInternal) => (isInternal ? " kjp-info--internal kj-tone-internal" : "");

/** Click + keyboard props for a div that acts as a button. */
export function pressProps(onClick, disabled) {
  if (!onClick || disabled) return disabled ? { "aria-disabled": true } : {};
  return {
    role: "button",
    tabIndex: 0,
    onClick,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick(e);
      }
    },
  };
}

// The design's pastel avatar fills, picked per name so a person keeps their colour.
const PASTEL_COUNT = 7;
export const pastelIndex = (key = "") => {
  let h = 0;
  const s = String(key || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % PASTEL_COUNT;
};

export const initialsOf = (name = "") =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

const hasPhoto = (src) =>
  typeof src === "string" &&
  src.trim() !== "" &&
  !/head\.svg$|profile\.png$|(^|\/)user\.png$/i.test(src.trim()) &&
  !/\/(null|undefined)$/.test(src.trim());

/** The backend sends /user/:id/info positions in capitals; show them as written words. */
export function readablePosition(pos) {
  const str = typeof pos === "string" ? pos.trim() : "";
  if (!str || str !== str.toUpperCase() || !/[A-ZÄÖÜ]{4,}/.test(str)) return str;
  return str.toLowerCase().replace(/(^|[\s\-/(])([a-zäöüß])/g, (m, sep, ch) => sep + ch.toUpperCase());
}

/** Position formatter for roleLineOf: the app's translated roles first, else the readable raw value. */
export const positionFormatter = (strings) => (pos) => {
  const raw = typeof pos === "string" ? pos.trim() : "";
  if (!raw) return "";
  let translated = "";
  try {
    translated = convertPosition(strings, raw);
  } catch (_) {
    translated = "";
  }
  return translated || readablePosition(raw);
};

/** Your own name for the member lists (design: the full name, no "Me"). */
export function myFullName(me) {
  if (!me) return "";
  const full = typeof me.fullName === "string" ? me.fullName.trim() : "";
  if (full) return full;
  return [me.title, me.firstName, me.lastName]
    .map((x) => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean)
    .join(" ");
}

/**
 * Count of the chat's media, links and docs (the rows card's value): the media
 * endpoint asked for one item, whose `totalChatMedia` is the total. null until it
 * arrives and on any failure.
 */
export function useChatMediaCount(groupId, viewByOwner) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (!groupId) return undefined;
    let live = true;
    const owner = !!viewByOwner;
    callAPI(
      `/group/${groupId}/media?viewByOwner=${owner}&viewByCompanyOwner=${owner}&page=1&limit=1&messageType=image,video,link,doc`,
      {},
      "GET"
    )
      .then((res) => {
        const data = res && res.data;
        const n = data && data.status ? Number(data.totalChatMedia) : NaN;
        if (live && Number.isFinite(n) && n >= 0) setCount(n);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [groupId, viewByOwner]);
  return count;
}

/**
 * Avatar. kind 'person' (round photo, else pastel + initials; optional online dot) ·
 * 'self' (My Space: round accent-soft tile with chat_paste_go) · 'room' (radius-22
 * accent-soft tile with the room initials, or its photo) · 'group' (radius-22 chip
 * tile with the group glyph). size 80 (hero) or 40 (member rows).
 */
export function InfoAvatar({ kind = "person", name = "", src, size = 40, online = false, className = "" }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  const photo = hasPhoto(src) && !failed && (kind === "person" || kind === "room");
  const pastel = kind === "person" ? ` kjp-ava--p${pastelIndex(name)}` : "";
  let inner;
  if (kind === "self") inner = <span className="material-symbols-outlined kjp-ava__glyph">chat_paste_go</span>;
  else if (kind === "group") inner = <span className="material-symbols-outlined kjp-ava__glyph">group</span>;
  else inner = <span className="kjp-ava__initials">{initialsOf(name) || "·"}</span>;
  return (
    <span className={`kjp-ava kjp-ava--${kind} kjp-ava--s${size}${pastel}${className ? ` ${className}` : ""}`} aria-hidden="true">
      {inner}
      {photo ? <img className="kjp-ava__img" src={src} alt="" draggable={false} onError={() => setFailed(true)} /> : null}
      {online ? <span className="kjp-ava__online" /> : null}
    </span>
  );
}

/**
 * Hero (design: 80 avatar, 8 apart, 20/26 600 name, 14/20 muted sub). `trailing`
 * sits after the name (the admin's edit pencil); `onClick` opens the person.
 */
export function InfoHero({ kind, name, src, sub, trailing, onClick, title }) {
  const press = pressProps(onClick);
  return (
    <div className="kjp-info-hero">
      <div className={`kjp-info-hero__avatar${onClick ? " is-pressable" : ""}`} {...press} title={onClick ? title || name : undefined}>
        <InfoAvatar kind={kind} name={name} src={src} size={80} />
      </div>
      <div className="kjp-info-hero__title">
        <div className={`kjp-info-hero__name${onClick ? " is-pressable" : ""}`} {...press} title={onClick ? title || name : undefined}>
          {name}
        </div>
        {trailing}
      </div>
      {sub ? <div className="kjp-info-hero__sub">{sub}</div> : null}
    </div>
  );
}

/**
 * Action tiles (design: 4 equal columns, gap 8; each 68 tall, radius 14, 1px line,
 * white, 24 glyph over 600 12/15). Always a quarter of the width, so 3 tiles leave
 * the fourth column empty. tiles [{ key, icon, label, onClick, active, ariaLabel }].
 */
export function InfoTiles({ tiles }) {
  const list = (tiles || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="kjp-info-tiles">
      {list.map((t) => (
        <div
          key={t.key}
          className={`kjp-info-tile${t.active ? " is-active" : ""}`}
          {...pressProps(t.onClick)}
          aria-label={t.ariaLabel || t.label}
          aria-pressed={t.active != null ? !!t.active : undefined}
          title={t.ariaLabel || t.label}
        >
          <span className="material-symbols-outlined kjp-info-tile__icon">{t.icon}</span>
          <div className="kjp-info-tile__label">{t.label}</div>
        </div>
      ))}
    </div>
  );
}

/** Card (white, 1px line, radius 16) whose children are split by 1px lines. */
export const InfoCard = React.forwardRef(function InfoCard({ children, className = "" }, ref) {
  const items = React.Children.toArray(children).filter(Boolean);
  if (!items.length) return null;
  return (
    <div ref={ref} className={`kjp-info-card${className ? ` ${className}` : ""}`}>
      {items.map((c, i) => (
        <div key={c.key || i} className="kjp-info-card__item">
          {c}
        </div>
      ))}
    </div>
  );
});

/** Row (design: padding 12 14, 22 muted glyph, 500 15/20 label, 600 13 value, 20 faint chevron). */
export function InfoRow({ icon, label, value, onClick, trailing, chevron, danger }) {
  const showChevron = chevron !== undefined ? chevron : !!onClick && !trailing;
  return (
    <div className={`kjp-info-row${onClick ? " is-pressable" : ""}${danger ? " is-danger" : ""}`} {...pressProps(onClick)}>
      {icon ? <span className="material-symbols-outlined kjp-info-row__icon">{icon}</span> : null}
      <div className="kjp-info-row__label">{label}</div>
      {value != null && value !== "" ? <div className="kjp-info-row__value">{value}</div> : null}
      {trailing}
      {showChevron ? <span className="material-symbols-outlined kjp-info-row__chevron">keyboard_arrow_right</span> : null}
    </div>
  );
}

/**
 * A setting with a switch (e-mail notifications, automatic export): 22 glyph, label
 * with the (i) button that shows `note` in the app's hover card (InfoTip), the web switch.
 * `onRowClick` = a tap on a disabled row (opens the note under the row, as before).
 * `children` = the expanded panel.
 */
export function InfoToggleRow({ icon, label, checked, disabled, dim, onToggle, id, isInternal, infoOpen, onInfo, note, onRowClick, children }) {
  return (
    <div className="kjp-info-setting">
      <div className={`kjp-info-row${onRowClick ? " is-soft" : ""}`} onClick={onRowClick}>
        <span className={`material-symbols-outlined kjp-info-row__icon${dim ? " is-dim" : ""}`}>{icon}</span>
        <div className={`kjp-info-row__label kjp-info-row__label--inline${dim ? " is-dim" : ""}`}>
          <div className="kjp-info-row__text">{label}</div>
          {onInfo && note ? <InfoTip title={label} text={note} disabled={dim} className="kjp-info-row__info" /> : null}
        </div>
        <div className="kjp-info-row__switch" onClick={(e) => e.stopPropagation()}>
          <ModernToggleSwitch checked={!!checked} onChange={onToggle} id={id} disabled={!!disabled} variant={isInternal ? "green" : "orange"} />
        </div>
      </div>
      {infoOpen && note ? <div className="kjp-info-note">{note}</div> : null}
      {children}
    </div>
  );
}

/** "MEMBERS · N" caption (12/16 700 +.08em muted caps) with the optional Add action. */
export function InfoCaption({ title, action, actionIcon, onAction }) {
  return (
    <div className="kjp-info-caption">
      <div className="kjp-info-caption__title">{title}</div>
      {action ? (
        <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm kjp-info-caption__action" onClick={onAction}>
          {actionIcon ? <span className="material-symbols-outlined kjp-info-caption__icon">{actionIcon}</span> : null}
          {action}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Member row (design: padding 10 14, 40 avatar with the online dot, 600 15/20 name,
 * the Admin tag, 500 13/18 muted "role · company" line, the External tag).
 * m = { key, name, src, adminLabel, roleLine, hidden, hiddenLabel, presence { online, label }, externalLabel, teamsLabel }.
 * teamsLabel: a Teams guest of a Kjuup + Teams group (Teams logo + "Teams" tag).
 */
export function InfoMemberRow({ m, onClick, active }) {
  const sub = m.hiddenLabel || m.roleLine || (m.presence ? m.presence.label : null) || null;
  return (
    <div
      className={`kjp-info-member${m.hidden ? " is-dim" : ""}${onClick ? " is-pressable" : ""}${active ? " is-active" : ""}`}
      {...pressProps(onClick)}
      aria-haspopup={onClick && m.menu ? "menu" : undefined}
      aria-expanded={onClick && m.menu ? !!active : undefined}
    >
      <InfoAvatar kind="person" name={m.name} src={m.src} size={40} online={!!(m.presence && m.presence.online)} />
      <div className="kjp-info-member__text">
        <div className="kjp-info-member__top">
          <div className="kjp-info-member__name">{m.name}</div>
          {m.adminLabel ? <div className="kjp-info-tag kjp-info-tag--new">{m.adminLabel}</div> : null}
        </div>
        {sub ? <div className="kjp-info-member__sub">{sub}</div> : null}
      </div>
      {m.externalLabel ? <div className="kjp-info-tag kjp-info-tag--muted">{m.externalLabel}</div> : null}
      {m.teamsLabel ? (
        <div className="kjp-info-tag kjp-info-tag--teams">
          <img src={teamsLogo} alt="" className="kjp-info-tag__logo" />
          {m.teamsLabel}
        </div>
      ) : null}
    </div>
  );
}

/** "Leave chat" (design: 50 tall, radius 14, 1px line, white, 600 15 label with a 22 glyph). tone err | ink. */
export function InfoLeaveButton({ icon = "logout", label, tone = "err", onClick }) {
  return (
    <div className={`kjp-info-leave kjp-info-leave--${tone}`} {...pressProps(onClick)}>
      <span className="material-symbols-outlined kjp-info-leave__icon">{icon}</span>
      <div className="kjp-info-leave__label">{label}</div>
    </div>
  );
}

/** 32 pencil next to an editable name / description. */
export function InfoEditPencil({ onClick, label }) {
  return (
    <button type="button" className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kjp-info-pencil" onClick={onClick} aria-label={label} title={label}>
      <span className="material-symbols-outlined">edit</span>
    </button>
  );
}

/** Inline edit box (group name / description): field, the char note, × and ✓. */
export function InfoEditBox({ value, onChange, placeholder, maxLength, multiline, note, onSave, onCancel, saveLabel, cancelLabel, inputRef }) {
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (onCancel) onCancel();
    } else if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      if (onSave) onSave();
    }
  };
  return (
    <div className="kjp-info-edit">
      {multiline ? (
        <textarea
          ref={inputRef}
          className="kjp-info-edit__input is-multi"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={4}
          autoFocus
          onKeyDown={onKeyDown}
        />
      ) : (
        <input
          ref={inputRef}
          type="text"
          className="kjp-info-edit__input"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          autoFocus
          onKeyDown={onKeyDown}
        />
      )}
      <div className="kjp-info-edit__foot">
        <div className="kjp-info-edit__note">{note || ""}</div>
        <button type="button" className="kj-btn kj-btn--icon kj-btn--round kj-btn--neutral kjp-info-edit__btn kjp-info-edit__btn--cancel" onClick={onCancel} aria-label={cancelLabel} title={cancelLabel}>
          <span className="material-symbols-outlined">close</span>
        </button>
        <button type="button" className="kj-btn kj-btn--icon kj-btn--round kj-btn--primary kjp-info-edit__btn" onClick={onSave} aria-label={saveLabel} title={saveLabel}>
          <span className="material-symbols-outlined">check</span>
        </button>
      </div>
    </div>
  );
}

/**
 * Away (unavailability) banner in the Pro look: warn tint with `event_busy`, the
 * member's name (groups) and message, and the urgent-contact link.
 */
export function InfoAwayBanner({ members, showName, contactLabel, onContactReplacement }) {
  const list = Array.isArray(members) ? members : [];
  if (!list.length) return null;
  return (
    <div className="kjp-info-away-list">
      {list.map((member) => {
        const endDateStr = member.endDate ? new Date(member.endDate).toLocaleDateString() : "";
        const message = member.message ? String(member.message).replace(/\{endDate\}/g, endDateStr) : "";
        const name = showName ? member.userName || member.userFullName || "" : "";
        const replacement = member.replacementUserId && member.replacementUserName ? member.replacementUserName : null;
        return (
          <div key={member.userId} className="kjp-info-away" role="status">
            <span className="material-symbols-outlined kjp-info-away__icon">event_busy</span>
            <div className="kjp-info-away__text">
              <div className="kjp-info-away__title">
                {name ? `${name}: ` : ""}
                {message}
              </div>
              {replacement ? (
                <div className="kjp-info-away__link" {...pressProps(() => onContactReplacement && onContactReplacement(member))}>
                  {`${contactLabel} – `}
                  <strong className="kjp-info-away__name">{replacement}</strong>
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const MENU_GAP = 6;
const MENU_MARGIN = 8;

/**
 * Small action menu anchored to an element (a member row): rendered in a portal so
 * the scroll area and the card do not clip it; closes on outside click, Escape,
 * scroll and resize. items [{ key, icon, label, onClick, danger, selected }].
 */
export function InfoPopoverMenu({ anchorEl, title, items, onClose, isInternal }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!anchorEl || !ref.current) return;
    const a = anchorEl.getBoundingClientRect();
    const m = ref.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = a.right - m.width - 12;
    left = Math.max(MENU_MARGIN, Math.min(left, vw - m.width - MENU_MARGIN));
    let top = a.bottom + MENU_GAP;
    if (top + m.height > vh - MENU_MARGIN) top = Math.max(MENU_MARGIN, a.top - m.height - MENU_GAP);
    setPos({ top: Math.round(top), left: Math.round(left) });
  }, [anchorEl]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const onScroll = (e) => {
      if (ref.current && e && e.target && ref.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [anchorEl, onClose]);

  useEffect(() => {
    const first = ref.current && ref.current.querySelector('[role="menuitem"]');
    if (first) first.focus();
  }, []);

  const onMenuKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const nodes = ref.current ? Array.from(ref.current.querySelectorAll('[role="menuitem"]')) : [];
    if (!nodes.length) return;
    const at = nodes.indexOf(document.activeElement);
    const next = e.key === "ArrowDown" ? (at + 1) % nodes.length : (at - 1 + nodes.length) % nodes.length;
    nodes[next].focus();
  };

  const menu = (
    <div
      ref={ref}
      className={`kjp-info-menu${isInternal ? " kjp-info-menu--internal" : ""}${pos ? " is-placed" : ""}`}
      style={pos ? { "--kjp-menu-top": `${pos.top}px`, "--kjp-menu-left": `${pos.left}px` } : undefined}
      role="menu"
      aria-label={title}
      onKeyDown={onMenuKey}
    >
      {title ? <div className="kjp-info-menu__title">{title}</div> : null}
      {(items || []).filter(Boolean).map((it) => (
        <div
          key={it.key}
          role="menuitem"
          tabIndex={-1}
          className={`kjp-info-menu__item${it.danger ? " is-danger" : ""}`}
          onClick={() => {
            onClose();
            if (it.onClick) it.onClick();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClose();
              if (it.onClick) it.onClick();
            }
          }}
        >
          <span className="material-symbols-outlined kjp-info-menu__icon">{it.icon}</span>
          <div className="kjp-info-menu__label">{it.label}</div>
        </div>
      ))}
    </div>
  );
  return ReactDOM.createPortal(menu, document.body);
}
